import { useEffect } from 'react';
import { useServices } from 'core/common/hooks';
import { importLottie, loadAnimationData } from 'core/common/ui/widgets/AnimatedImage';
import { ScreenAttributes, VisualAssetType } from 'core/flow/entities';

type AnimationsPreloaderProps = {
  attributes: ScreenAttributes[];
};

const isAnimationAsset = (value: object): value is { url: string } =>
  'type' in value &&
  value.type === VisualAssetType.ANIMATION &&
  'url' in value &&
  typeof value.url === 'string' &&
  !!value.url;

const collectAnimationUrls = (value: unknown, urls: Set<string>) => {
  if (typeof value !== 'object' || value === null) return;

  if (isAnimationAsset(value)) {
    urls.add(value.url);
    return;
  }

  Object.values(value).forEach((nestedValue) => collectAnimationUrls(nestedValue, urls));
};

const getAnimationUrls = (attributes: ScreenAttributes[]) => {
  const urls = new Set<string>();

  collectAnimationUrls(attributes, urls);

  return [...urls];
};

export const AnimationsPreloader = ({ attributes }: AnimationsPreloaderProps) => {
  const { contentManagerService } = useServices();

  useEffect(() => {
    const animationUrls = getAnimationUrls(attributes);

    if (!animationUrls.length) return;

    importLottie();
    animationUrls.forEach((url) => loadAnimationData(contentManagerService, url));
  }, [contentManagerService, attributes]);

  return null;
};
